import API from 'util/API';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Uber Eats API endpoints
 */

// Get Uber OAuth URL for restaurant
export const getUberAuthUrl = async (restaurantId) => {
  const response = await API.get(`/uber/auth-url?restaurantId=${restaurantId}`);
  return response.data;
};

// Get Uber integration configuration
export const getUberIntegrationConfig = async () => {
  const response = await API.get('/uber/integration-config');
  return response.data;
};

// Delete/disconnect Uber integration
export const disconnectUberIntegration = async () => {
  const response = await API.delete('/uber/integration-config');
  return response.data;
};

// Upload menu to Uber Eats
export const uploadUberEatsMenu = async (menuId, type) => {
  const response = await API.post(`/uploadUberEatsMenu/${menuId}/${type}`);
  return response.data;
};

// Connect Uber stores
export const connectUberStores = async (selectedStores, code) => {
  const response = await API.post('/uber/connect-stores', { stores: selectedStores, code: code });
  return response.data;
};

/**
 * React Query Hooks
 */

// Hook to get Uber integration config
export const useUberIntegrationConfig = (enabled = true) => {
  return useQuery({
    queryKey: ['uberIntegrationConfig'],
    queryFn: getUberIntegrationConfig,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

// Hook to disconnect Uber integration
export const useDisconnectUberIntegration = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: disconnectUberIntegration,
    onSuccess: () => {
      // Invalidate and refetch integration config
      queryClient.invalidateQueries({ queryKey: ['uberIntegrationConfig'] });
      // You might also want to refetch restaurant data if it stores the uberEatsStoreId
      queryClient.invalidateQueries({ queryKey: ['selectedRestaurant'] });
    },
    onError: (error) => {
      console.error('Error disconnecting Uber integration:', error);
    }
  });
};

// Hook to get Uber auth URL
export const useUberAuthUrl = () => {
  return useMutation({
    mutationFn: getUberAuthUrl,
    onError: (error) => {
      console.error('Error getting Uber auth URL:', error);
    }
  });
};

// Hook to upload menu to Uber Eats
export const useUploadUberEatsMenu = () => {
  return useMutation({
    mutationFn: ({ menuId, type }) => uploadUberEatsMenu(menuId, type),
    onError: (error) => {
      console.error('Error uploading menu to Uber Eats:', error);
    }
  });
};

// Hook to connect Uber stores
export const useConnectUberStores = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: connectUberStores,
    onSuccess: () => {
      // Invalidate and refetch integration config
      queryClient.invalidateQueries({ queryKey: ['uberIntegrationConfig'] });
      // You might also want to refetch restaurant data if it stores the uberEatsStoreId
      queryClient.invalidateQueries({ queryKey: ['selectedRestaurant'] });
    },
    onError: (error) => {
      console.error('Error connecting Uber stores:', error);
    }
  });
};